import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import fullLogo from '../assets/logotwo.png';

const LINKS = [
  { name: 'Excursions', path: '/excursions' },
  { name: 'Stays', path: '/directory' },
  { name: 'Car Hire', path: '/car-hire' },
  { name: 'Blog', path: '/blog' },
  { name: 'About', path: '/about' },
];

const SAFARI_MENU = [
  { name: 'All safaris', path: '/safaris' },
  { name: 'Kenya', path: '/safaris/kenya' },
  { name: 'Tanzania', path: '/safaris/tanzania' },
  { name: 'Rwanda', path: '/safaris/rwanda' },
  { name: 'South Africa', path: '/safaris/southafrica' },
  { name: 'Zanzibar', path: '/safaris/zanzibar' },
  { name: 'Destinations & parks', path: '/destinations' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation();

  // Only the homepage opens on a full-bleed dark hero behind the fixed nav.
  const isHomePage = location.pathname === '/';
  const onDark = isHomePage && !isScrolled; // transparent nav with light text at the top of home

  const isActive = (path) => (path === '/' ? location.pathname === path : location.pathname.startsWith(path));
  const isSafariActive = location.pathname.startsWith('/safaris') || location.pathname.startsWith('/destinations');

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileMenu(false); }, [location]);

  const linkCls = (active) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      active
        ? 'text-hunyadi-yellow'
        : onDark
          ? 'text-white/90 hover:text-hunyadi-yellow'
          : 'text-cafe-noir/80 hover:text-brown-sugar'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        onDark ? 'bg-gradient-to-b from-black/55 to-transparent' : 'bg-eggshell/95 backdrop-blur-sm border-b border-cafe-noir/10'
      }`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="Jambo Travel House — Home">
            <img src={fullLogo} alt="Jambo Travel House Holidays" className="h-11 object-contain" />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {/* Safaris dropdown */}
            <div className="relative group">
              <Link to="/safaris" className={`${linkCls(isSafariActive)} inline-flex items-center gap-1`} aria-haspopup="true">
                Safaris <ChevronDown size={15} className="transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
              </Link>
              <div className="absolute left-0 mt-1 w-60 rounded-xl border border-cafe-noir/10 bg-eggshell py-2 opacity-0 invisible transition-opacity duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                {SAFARI_MENU.map((item) => (
                  <Link key={item.path} to={item.path} className={`block px-4 py-2 text-sm transition-colors ${isActive(item.path) && item.path !== '/safaris' ? 'text-brown-sugar' : 'text-cafe-noir/80 hover:text-brown-sugar hover:bg-white/60'}`}>
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {LINKS.map((l) => (
              <Link key={l.path} to={l.path} className={linkCls(isActive(l.path))}>{l.name}</Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:block">
            <Link to="/enquire" className="rounded-full bg-hunyadi-yellow text-cafe-noir px-6 py-2.5 text-sm font-medium hover:bg-brown-sugar hover:text-white transition-colors">
              Plan your trip
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileMenu((v) => !v)}
            className={`md:hidden p-2 rounded-md transition-colors ${onDark ? 'text-white hover:bg-white/10' : 'text-cafe-noir hover:bg-cafe-noir/5'}`}
            aria-expanded={mobileMenu}
            aria-controls="mobile-menu"
            aria-label={mobileMenu ? 'Close menu' : 'Open menu'}
          >
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        id="mobile-menu"
        className={`md:hidden absolute left-0 right-0 top-20 bg-eggshell border-t border-cafe-noir/10 max-h-[80vh] overflow-y-auto transition duration-300 ${
          mobileMenu ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'
        }`}
      >
        <div className="px-5 py-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brown-sugar mb-2">Safaris</p>
            <div className="grid gap-1">
              {SAFARI_MENU.map((item) => (
                <Link key={item.path} to={item.path} className="block py-1.5 text-cafe-noir/80 hover:text-brown-sugar">{item.name}</Link>
              ))}
            </div>
          </div>
          <div className="border-t border-cafe-noir/10 pt-5 grid gap-1">
            {LINKS.map((l) => (
              <Link key={l.path} to={l.path} className="block py-1.5 text-cafe-noir/80 hover:text-brown-sugar font-medium">{l.name}</Link>
            ))}
          </div>
          <Link to="/enquire" className="block text-center rounded-full bg-hunyadi-yellow text-cafe-noir px-5 py-3 font-medium hover:bg-brown-sugar hover:text-white transition-colors">
            Plan your trip
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
